import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import { FC } from "react";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  OptionsDataType,
} from "../../../../../components";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../../helpers/constants";
import {
  TableActionType,
  TableActions,
} from "../../../../../helpers/types/common";
import { buildActionMenuItems } from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import {
  CreateClientDeliverablesFormKeys,
  CreateClientDeliverablesTabProps,
} from "../modelsAndSchemas/models";
export const pageId = "Create-Client-Activities-tab";

export const CreateClientDeliverablesTab: FC<
  CreateClientDeliverablesTabProps
> = ({
  rows,
  control,
  tableRef,
  isView,
  uniqueFilters,
  filteredResults,
  onDeliverableViewClick,
  deleteAction,
}) => {
  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const onActionClick = (action: TableActionType, row: any) => {
    if (action === TableActions.edit) {
      onDeliverableViewClick(row, true);
    } else {
      deleteAction(row);
    }
  };
  const renderFormControl = (
    control: any,
    name: keyof typeof CreateClientDeliverablesFormKeys,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={options}
        value=""
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm = [
    <FormController
      control={control}
      name={CreateClientDeliverablesFormKeys.name}
      key={CreateClientDeliverablesFormKeys.name}
    >
      <MuiTextField
        id={`${pageId}-${CreateClientDeliverablesFormKeys.name}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFormControl(
      control,
      CreateClientDeliverablesFormKeys.deliverableType,
      uniqueFilters.deliverableType,
      commonLabels.selectPlaceHolder,
      `${pageId}-${CreateClientDeliverablesFormKeys.deliverableType}`,
    ),

    renderFormControl(
      control,
      CreateClientDeliverablesFormKeys.units,
      uniqueFilters.units,
      commonLabels.selectPlaceHolder,
      `${pageId}-${CreateClientDeliverablesFormKeys.units}`,
    ),
    <></>,
    <></>,
  ];

  const columns = [
    {
      id: CreateClientDeliverablesFormKeys.name,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.deliverableName,
      ),
      render: (row: any) =>
        !row.name ? null : (
          <MuiLink onClick={() => onDeliverableViewClick(row)}>
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link ellipsis-with-tooltip"
                text={row.name}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
      isSortVisible: true,
      className: "cell-deliverable-name",
    },
    {
      id: CreateClientDeliverablesFormKeys.deliverableType,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.deliverableType,
      ),
      isSortVisible: true,
      className: "cell-deliverable-type",
    },
    {
      id: CreateClientDeliverablesFormKeys.units,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
        activitiesAndDeliverablesSiteCoreKeys.deliverablesUnits,
      ),
      isSortVisible: true,
      className: "cell-units",
    },
    {
      id: CreateClientDeliverablesFormKeys.measurements,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
        activitiesAndDeliverablesSiteCoreKeys.deliverablesMeasurement,
      ),
      isSortVisible: true,
      className: "cell-measurement",
    },
    {
      id: "action",
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.defaultActivityKey,
        activitiesAndDeliverablesSiteCoreKeys.action,
      ),
      render: (row: any) => {
        const clientDeliverablesActions = [
          {
            permissionKey: MODULE_PERMISSIONS.EDIT_CLIENT_SPECIFIC_ACTIVITIES,
            create: () => ({
              onClick: () => onActionClick(TableActions.edit, row),
              label: commonLabels.edit,
              dataTestId: "editDeliverable",
              ariaLabel: commonLabels.edit,
            }),
          },
          {
            permissionKey: MODULE_PERMISSIONS.DELETE_CLIENT_SPECIFIC_ACTIVITIES,
            create: () => ({
              onClick: () => onActionClick(TableActions.delete, row),
              label: commonLabels.delete,
              dataTestId: "deleteDeliverable",
              ariaLabel: commonLabels.delete,
            }),
          },
        ];

        const clientDeliverablesMenuItems = buildActionMenuItems(
          row,
          clientDeliverablesActions,
          hasModulePermission,
        );

        return clientDeliverablesMenuItems ? (
          <MuiOptionMenu menuItems={clientDeliverablesMenuItems} />
        ) : null;
      },
    },
  ];

  return (
    <MuiGrid container data-testid="search-activities">
      <MuiGrid size={{ xs: 12 }} className="header-right">
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={
            isView ? tableHeaderForm.slice(0, -1) : tableHeaderForm
          }
          columns={isView ? columns.slice(0, -1) : columns}
          tableResults={[...filteredResults]}
          noDataInApi={rows.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
            activitiesAndDeliverablesSiteCoreKeys.deliverablesNotAvailable,
          )}
          priorityResults={[]}
          isLastColumnSticky={!isView}
        />
      </MuiGrid>
    </MuiGrid>
  );
};
